import { useEffect, useState, useRef } from "react";
import { Link, useParams, useOutletContext } from "react-router-dom";
import { Space, Table, Button, Modal, Input, Form, Radio, Pagination, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { cloneDeep } from "lodash-es";
import { useSize } from "ahooks";
import api from "@/api";
import { format } from "@/utils";
import { publicPath } from "@/utils/base";

const { confirm } = Modal;

import dataJson from "@/../public/data.json";

const INPUT_HEIGHT = 38;
const prefix = "profile";
import "./index.less";

function Certificate() {
  const {
    user,
    certificate,
    app_detail,
    add_create,
    confirmText,
    cancelText,
    req_err,
    delete_success,
    download_fail,
    name_label,
  } = dataJson;

  const [open, setOpen] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [atomicDetail, setAtomicDetail] = useState<any>();
  const [certificateArr, setCertificateArr] = useState<any[]>([]);
  const [typeArr, setTypeArr] = useState<any[]>(app_detail.type_arr);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [columns, setColumns] = useState(app_detail.table_columns);
  const [current, setCurrent] = useState(1);
  const [total, setTotal] = useState(0);
  const [form] = Form.useForm();
  const tableWrapRef = useRef<any>();

  const { type, id } = useParams();
  const { isTampered = false } = useOutletContext<{ isTampered?: boolean }>() || {};

  const size = useSize(tableWrapRef);
  const releaseDetail = useSelector((state: any) => state.releaseDetail);

  const typeObj: any = app_detail.type_obj;
  const typeItem = typeObj[type!];

  const req = {
    limit: 10,
    page: 1,
  };

  const initColumns = () => {
    const tableColumnsCopy = cloneDeep(app_detail.table_columns);
    const columnsCopy = tableColumnsCopy.map((item: any) => {
      if (item.key === "developerCertificateName") {
        item.render = (text: any, record: any) => {
          if (record?.developerCertificateAbolishFlag === "0") {
            return <span>{text}</span>;
          } else {
            return <span style={{ color: "rgba(0,0,0,0.4)" }}>{app_detail.repealed}</span>;
          }
        };
      } else if (item.key === "status") {
        item.render = (text: any) => {
          const statusText = text === "0" ? certificate.normal : certificate.abnormal;
          return <span>{statusText}</span>;
        };
      } else if (item.key === "type") {
        item.render = (text: any) => {
          const typeText = app_detail.type_arr.find((ele: any) => ele.key === text)!.label;
          return <span>{typeText}</span>;
        };
      } else if (item.key === "createTime") {
        item.render = (text: any) => {
          const createTimeText = format(new Date(Number(text)), "yy-MM-dd");
          return <span>{createTimeText}</span>;
        };
      } else if (item.key === "expirationDate") {
        item.render = (text: any) => {
          const expirationDateText = format(new Date(Number(text)), "yy-MM-dd");
          return <span>{expirationDateText}</span>;
        };
      } else if (item.key === "action") {
        item.render = (text: any, record: any) => {
          const btnArr = item.btns;
          const btnShow = record?.status === "0" ? "normal" : "unNormal";
          return (
            <Space size={48}>
              {btnArr[btnShow].map((ele: any) => {
                return (
                  <a key={ele.key} onClick={() => handleClick(ele.key, record)}>
                    {ele.label}
                  </a>
                );
              })}
            </Space>
          );
        };
      }
      return item;
    });
    setColumns(columnsCopy);
  };

  const paginationChange = (e: number) => {
    req.page = e;
    getList();
    setCurrent(e);
  };

  const getList = async () => {
    const res: any = await api.appCertList({ ...req, appId: id });
    if (res.code === 200) {
      const resList = res.data.datas.map((item: any) => ({ ...item, key: item.id }));

      // 查询当前应用下是否有已发布的应用证书
      const resp: any = await api.appCertCheckRelease({ appId: id });
      const isRelease = !!resp?.data;
      let typeArrCopy = cloneDeep(app_detail.type_arr);
      if (isRelease) {
        typeArrCopy = app_detail.type_arr.map((item: any) => {
          return item.key === "RELEASE" ? { ...item, disabled: true } : item;
        });
      }
      setTypeArr(typeArrCopy);
      setDataSource(resList);
      setTotal(Number(res.data.total));
    } else {
      message.error(res.msg || req_err);
    }
  };

  const handleAdd = async () => {
    const res: any = await api.certificateSelect();
    if (res.code === 200) {
      setCertificateArr(res.data);
      setOpen(true);
    }
  };

  const handleDownload = async (item: any) => {
    const res: any = await api.appCertDownload(item);
    if (res.code === 500) {
      message.error(download_fail);
      return;
    }
    const url = window.URL.createObjectURL(new Blob([res]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", item.name + ".p7b");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = (item: any) => {
    confirm({
      title: app_detail.delete_profile,
      icon: null,
      width: 328,
      centered: true,
      closable: true,
      closeIcon: <CloseOutlined />,
      okText: confirmText,
      cancelText: cancelText,
      content: (
        <div className="delete-modal-content">
          {app_detail.is_delete}&quot;{item.name}&quot;
        </div>
      ),
      onOk: async () => {
        const res: any = await api.appCertDelete(item);
        if (res.code === 200) {
          message.success(delete_success);
          getList();
        } else {
          message.error(res.msg || req_err);
        }
      },
    });
  };

  const handleClick = (type: string, record: any) => {
    if (type === "download") {
      handleDownload(record);
    } else if (type === "delete") {
      handleDelete(record);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);
      const res: any = await api.appCertAdd({
        ...values,
        appId: id,
      });
      if (res.code === 200) {
        setOpen(false);
        setConfirmLoading(false);
        getList();
      } else {
        setConfirmLoading(false);
        message.error(res.msg || req_err);
      }
    } catch (errorInfo: any) {
      console.error(errorInfo);
    }
  };
  const handleCancel = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (releaseDetail) {
      const resDataCopy = {
        ...releaseDetail,
        packageType: releaseDetail.subType.join("/"),
        createTimeText: format(new Date(Number(releaseDetail?.createTime)), "yyyy年MM月dd日 HH:mm:ss"),
        tpyeName: typeItem?.label,
      };
      setAtomicDetail(resDataCopy);
    }
  }, [releaseDetail]);

  useEffect(() => {
    initColumns();
    getList();
  }, []);

  return (
    <div className={prefix}>
      <div className={`${prefix}-content`}>
        <div className={`${prefix}-content-header`}>
          <div className={`${prefix}-content-header-left`}>
            <div>{app_detail.profile_title}</div>
            <div>{app_detail.profile_tip}</div>
          </div>
          <Button type="primary" style={{ width: 90 }} onClick={handleAdd} disabled={isTampered || total >= 30}>
            {add_create}
          </Button>
        </div>
        <div className={`${prefix}-content-table`} ref={tableWrapRef}>
          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            scroll={{ scrollToFirstRowOnChange: true, y: size?.height ? size?.height - 120 : 300 }}
          />
          {total > 10 ? (
            <Pagination
              simple
              pageSize={10}
              current={current}
              total={total}
              rootClassName={`${prefix}-content-table-pagination`}
              onChange={paginationChange}
            />
          ) : null}
        </div>
      </div>
      <Modal
        title={app_detail.add_title}
        centered
        open={open}
        width={328}
        destroyOnClose
        confirmLoading={confirmLoading}
        cancelText={cancelText}
        okText={add_create}
        onCancel={handleCancel}
        onOk={handleOk}
        maskClosable={false}
        cancelButtonProps={{ disabled: confirmLoading }}
        afterOpenChange={(open: boolean) => !open && form.resetFields()}
        className={`${prefix}-modal`}
      >
        <div>
          {app_detail.package_name}
          {atomicDetail?.packageName}
        </div>
        <Form className={`${prefix}-modal-form`} labelCol={{ span: 24 }} form={form} autoComplete="off">
          <Form.Item name="name" label={name_label} rules={[{ required: true, message: app_detail.name_placeholder }]}>
            <Input
              showCount
              maxLength={100}
              placeholder={app_detail.name_placeholder}
              style={{ height: INPUT_HEIGHT }}
            />
          </Form.Item>
          <Form.Item
            name="type"
            label={user.account_type}
            rules={[{ required: true, message: user.account_type_rule }]}
          >
            <Radio.Group>
              {typeArr.map((item: any) => {
                return (
                  <Radio value={item.key} key={item.key} disabled={item.disabled}>
                    {item.label}
                  </Radio>
                );
              })}
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="developerCertificateId"
            label={app_detail.select_certificate}
            rules={[{ required: true, message: app_detail.select_certificate }]}
          >
            {certificateArr?.length ? (
              <Radio.Group>
                {certificateArr.map((item: any) => {
                  return (
                    <Radio value={item.id} key={item.id}>
                      <div
                        title={item.name}
                        className={`${prefix}-developerCertificateId-radio-lable one-line-ellipsis`}
                      >
                        {item.name}
                      </div>
                    </Radio>
                  );
                })}
              </Radio.Group>
            ) : (
              <Link to={`${publicPath}/user/certificate`}>{certificate.add_certificate}</Link>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Certificate;
